import React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  MenuItem,
  Select,
  Button,
  TextField,
  Grid
} from "@mui/material";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function AdmReport() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [selectedOption, setSelectedOption] = React.useState("all");
  const [selectedClass, setSelectedClass] = React.useState("");
  const [selectedCaste, setSelectedCaste] = React.useState("");
  const [selectedGender, setSelectedGender] = React.useState("");
  const [selectedStream, setSelectedStream] = React.useState(0);
  const [selectedSession, setSelectedSession] = React.useState("");
  const [isEWS, setIsEWS] = React.useState("no");
  const [fromDate, setFromDate] = React.useState("");
  const [toDate, setToDate] = React.useState("");
  const [ddlClass, setDdlClass] = useState([]);
  const [ddlCaste, setDdlCaste] = useState([]);
  const [ddlGender, setDdlGender] = useState([]);
  const [ddlStream, setDdlStream] = useState([]);
  const [ddlSession, setDdlSession] = useState([]);
  const [schoolData, setSchoolData] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [isCasteFilter, setIsCasteFilter] = useState(false);
  const [isGenderFilter, setIsGenderFilter] = useState(false);
  const [isStreamFilter, setIsStreamFilter] = useState(false);
  const [isSessionFilter, setIsSessionFilter] = useState(false);
  const [error, setError] = useState(null);

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const fromDateRef = React.useRef(null);
  const toDateRef = React.useRef(null);

  const formatToDDMMYYYY = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const setDefaultDates = useCallback(() => {
    if (!isSessionFilter || !selectedSession) {
      const today = new Date();
      const defaultToDate = new Date(today);
      const defaultFromDate = new Date(today);
      defaultFromDate.setMonth(defaultFromDate.getMonth() - 1);

      const selectedSessionObj = ddlSession.find(
        (session) => session.financialYearID === selectedSession
      );
      let minDate, maxDate;
      if (selectedSessionObj) {
        const [startYear, endYear] =
          selectedSessionObj.finanacialYear.split("-");
        minDate = new Date(startYear, 3, 1);
        maxDate = new Date(endYear, 2, 31);
      } else {
        const currentYear = today.getFullYear();
        const financialYearStart = new Date(currentYear, 3, 1);
        const financialYearEnd = new Date(currentYear + 1, 2, 31);
        const todayIsInCurrentFinancialYear =
          today >= financialYearStart && today <= financialYearEnd;
        const startYear = todayIsInCurrentFinancialYear
          ? currentYear
          : currentYear - 1;
        const endYear = todayIsInCurrentFinancialYear
          ? currentYear + 1
          : currentYear;
        minDate = new Date(startYear, 3, 1);
        maxDate = new Date(endYear, 2, 31);
      }

      if (defaultFromDate < minDate) {
        defaultFromDate.setTime(minDate.getTime());
      }
      if (defaultToDate > maxDate) {
        defaultToDate.setTime(maxDate.getTime());
      }

      setFromDate(formatToDDMMYYYY(defaultFromDate));
      setToDate(formatToDDMMYYYY(defaultToDate));
    } else {
      const selectedSessionObj = ddlSession.find(
        (session) => session.financialYearID === selectedSession
      );
      if (selectedSessionObj) {
        const [startYear, endYear] =
          selectedSessionObj.finanacialYear.split("-");
        const minDate = new Date(startYear, 3, 1);
        const maxDate = new Date(endYear, 2, 31);
        setFromDate(formatToDDMMYYYY(minDate));
        setToDate(formatToDDMMYYYY(maxDate));
      }
    }
  }, [isSessionFilter, selectedSession, ddlSession]);

  const fetchSchoolDetails = async () => {
    try {
      const response = await fetch(`${apiUrl}/FeeReport/GetSchoolName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching school details: ${response.status}`);
      }
      const data = await response.json();
      setSchoolData(data);
    } catch (error) {
      console.error(error);
      setError("Failed to fetch school details");
    }
  };

  const fetchDdlClass = async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching classes: ${response.status}`);
      }
      const data = await response.json();
      setDdlClass(data);
    } catch (error) {
      console.error(error);
      setError("Failed to fetch classes");
    }
  };

  const fetchDdlCaste = async () => {
    try {
      const response = await fetch(`${apiUrl}/Caste/GetCaste`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      setDdlCaste(data);
    } catch (error) {
      console.error(error);
      setError("Failed to fetch castes");
    }
  };

  const fetchDdlGender = async () => {
    try {
      const response = await fetch(`${apiUrl}/Admission/ddlGender`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      setDdlGender(data);
    } catch (error) {
      console.error(error);
      setError("Failed to fetch genders");
    }
  };

  const fetchDdlStream = async () => {
    try {
      const response = await fetch(`${apiUrl}/Stream/GetAllStreams`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        throw new Error(`Error fetching streams: ${response.status}`);
      }
      const data = await response.json();
      setDdlStream(data);
    } catch (error) {
      console.error(error);
      setError("Failed to fetch streams");
    }
  };

  const fetchDdlSession = async () => {
    try {
      const response = await fetch(
        `${apiUrl}/ClassPromotion/GetFinancialYear`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) {
        throw new Error(`Error fetching financial years: ${response.status}`);
      }
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        console.error("Record Not Found");
        setError("No financial years found");
        return;
      }
      setDdlSession(data);

      const today = new Date();
      const currentYear = today.getFullYear();
      const nextYear = currentYear + 1;
      const financialYearStart = new Date(currentYear, 3, 1);
      const financialYearEnd = new Date(nextYear, 2, 31);
      let currentSessionString;
      if (today >= financialYearStart && today <= financialYearEnd) {
        currentSessionString = `${currentYear}-${nextYear}`;
      } else {
        currentSessionString = `${currentYear - 1}-${currentYear}`;
      }
      const currentSession = data.find(
        (item) => item.finanacialYear === currentSessionString
      );
      if (currentSession) {
        setSelectedSession(currentSession.financialYearID);
      }
    } catch (error) {
      console.error(error);
      setError("Failed to fetch sessions");
    }
  };

  const getFinancialYearRange = () => {
    const selectedSessionObj = ddlSession.find(
      (session) => session.financialYearID === selectedSession
    );
    if (!selectedSessionObj) return { minDate: null, maxDate: null };
    const [startYear, endYear] = selectedSessionObj.finanacialYear.split("-");
    const minDate = new Date(startYear, 3, 1);
    const maxDate = new Date(endYear, 2, 31);
    return { minDate, maxDate };
  };

  const handleOptionChange = (event) => setSelectedOption(event.target.value);
  const handleClassChange = (event) => setSelectedClass(event.target.value);
  const handleCasteChange = (event) => setSelectedCaste(event.target.value);
  const handleGenderChange = (event) => setSelectedGender(event.target.value);
  const handleStreamChange = (event) => {
    const value = Number(event.target.value);
    setSelectedStream(value);
  };
  const handleSessionChange = (event) => {
    setSelectedSession(event.target.value);
  };
  const handleEWSChange = (event) => setIsEWS(event.target.value);
  const handleCasteFilterToggle = () => {
    setIsCasteFilter((prev) => {
      const newValue = !prev;
      if (!newValue) setSelectedCaste("");
      return newValue;
    });
  };
  const handleGenderFilterToggle = () => {
    setIsGenderFilter((prev) => {
      const newValue = !prev;
      if (!newValue) setSelectedGender("");
      return newValue;
    });
  };
  const handleStreamFilterToggle = () => {
    setIsStreamFilter((prev) => {
      const newValue = !prev;
      if (!newValue) setSelectedStream(0);
      return newValue;
    });
  };
  const handleSessionFilterToggle = () => {
    setIsSessionFilter((prev) => {
      const newValue = !prev;
      if (!newValue) {
        setFromDate("");
        setToDate("");
      }
      return newValue;
    });
  };

  const handleFromDateChange = (event) => {
    const [year, month, day] = event.target.value.split("-");
    const newDate = new Date(year, month - 1, day);
    setFromDate(formatToDDMMYYYY(newDate));
  };

  const handleToDateChange = (event) => {
    const [year, month, day] = event.target.value.split("-");
    const newDate = new Date(year, month - 1, day);
    setToDate(formatToDDMMYYYY(newDate));
  };

  useEffect(() => {
    fetchDdlClass();
    fetchSchoolDetails();
    fetchDdlCaste();
    fetchDdlGender();
    fetchDdlStream();
    fetchDdlSession();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      setDefaultDates();
    }
  }, [selectedSession, isSessionFilter, setDefaultDates]);

  const formatDate = (dateString) => {
    return dateString;
  };

  const isGenerateDisabled = () => {
    if (isCasteFilter && !selectedCaste) return true;
    if (isGenderFilter && !selectedGender) return true;
    if (isStreamFilter && !selectedStream) return true;
    return false;
  };

  function summarizeTransportDetails(detailString) {
    if (!detailString || detailString.trim() === "" || detailString === "null") {
      return "-";
    }

    try {
      // Split into individual entries
      const entries = detailString.split(", ").filter(entry => entry.trim() !== "");

      if (entries.length === 0) return "-";

      // Extract route, months, and amount from first entry
      const firstEntry = entries[0];
      const match = firstEntry.match(/^(.+?) \((.+?)\) ([\d.]+)$/);

      if (!match) {
        return detailString.length > 25
          ? `${detailString.substring(0, 25)}...`
          : detailString;
      }

      const routeName = match[1].trim();
      const amount = match[3];

      // Get all unique months
      const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"];
      const months = [];

      entries.forEach(entry => {
        const monthMatch = entry.match(/\((.+?)\)/);
        if (monthMatch) months.push(monthMatch[1].trim());
      });

      // Sort months chronologically
      months.sort((a, b) => monthNames.indexOf(a) - monthNames.indexOf(b));

      // Get first and last month abbreviations
      const firstMonth = months[0].substring(0, 3);
      const lastMonth = months[months.length - 1].substring(0, 3);

      // Format with line breaks for PDF cell
      return `${routeName}\n${firstMonth}-${lastMonth}\nRs. ${amount}`;

    } catch (error) {
      console.error("Error formatting transport details:", error);
      return detailString.length > 25
        ? `${detailString.substring(0, 25)}...`
        : detailString;
    }
  }

  const generatePDF = useCallback(
    (
      data,
      schoolName,
      sessionYear,
      formattedFromDate,
      formattedToDate,
      className,
      casteName,
      genderName,
      streamName,
      ewsStatus
    ) => {
      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 15;
      const tableLeftMargin = 3; // Reduced left margin for table
      const maxWidth = pageWidth - 2 * margin;

      // Process logo and PDF content in a single async block
      (async () => {
        let logoDataUrl = null;
        try {
          // Hardcoded logo as fallback
          const hardcodedLogo = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=';
          logoDataUrl = hardcodedLogo;

          // Validate hardcoded image
          const img = new Image();
          const loadPromise = new Promise((resolve, reject) => {
            img.onload = () => {
              console.log("Hardcoded logo loaded successfully");
              resolve();
            };
            img.onerror = () => {
              console.error("Failed to load hardcoded logo image");
              reject(new Error('Failed to load hardcoded logo image'));
            };
            img.src = logoDataUrl;
          });
          await loadPromise;

          try {
            // Add white background with subtle shadow (left-aligned, reduced size, moved up)
            doc.setFillColor(240, 240, 240);
            doc.rect(margin - 2, 5, 34, 34, "F"); // Shadow background
            doc.setFillColor(255, 255, 255);
            doc.rect(margin, 7, 30, 30, "F"); // White background
            // Add hardcoded logo
            doc.addImage(logoDataUrl, 'PNG', margin + 2, 9, 26, 26);
            console.log("Hardcoded logo successfully added to PDF");
          } catch (error) {
            console.error('Error adding hardcoded logo to PDF:', error.message);
            throw error;
          }

          // Attempt API fetch for actual logo
          const token = sessionStorage.getItem("token");
          if (!token) {
            console.warn("No authentication token found in sessionStorage");
            throw new Error("No authentication token found");
          }

          const response = await fetch('https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token,
            },
            body: JSON.stringify({}),
          });

          if (!response.ok) {
            console.error(`API fetch failed: ${response.status} ${response.statusText}`);
            throw new Error(`API error: ${response.status} ${response.statusText}`);
          }

          const apiData = await response.json();
          console.log("API response data:", JSON.stringify(apiData, null, 2));

          const logoImg = apiData?.[0]?.logoImg;
          if (!logoImg || typeof logoImg !== 'string' || logoImg.trim() === '') {
            console.warn("No valid logo image found in API response");
            throw new Error("Invalid or missing logoImg in API response");
          }

          let cleanedBase64 = logoImg
            .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
            .replace(/\s/g, '')
            .replace(/\n/g, '');
          const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
          cleanedBase64 += '='.repeat(paddingLength);

          const isValidBase64 = /^[A-Za-z0-9+/=]+$/.test(cleanedBase64);
          if (!isValidBase64) {
            console.error('Invalid base64 string for logoImg:', cleanedBase64.substring(0, 100) + '...');
            throw new Error('Invalid base64 string for logoImg');
          }

          console.log('Logo base64 (first 100 chars):', cleanedBase64.substring(0, 100) + '...');
          console.log('Full logo base64 length:', cleanedBase64.length);

          logoDataUrl = `data:image/png;base64,${cleanedBase64}`;

          // Validate API image
          const apiImg = new Image();
          const apiLoadPromise = new Promise((resolve, reject) => {
            apiImg.onload = () => {
              console.log("API logo loaded successfully");
              resolve();
            };
            apiImg.onerror = () => {
              console.error("Failed to load API logo image");
              reject(new Error('Failed to load API logo image'));
            };
            apiImg.src = logoDataUrl;
          });
          await apiLoadPromise;

          try {
            // Add white background with subtle shadow (left-aligned, reduced size, moved up)
            doc.setFillColor(240, 240, 240);
            doc.rect(margin - 2, 5, 34, 34, "F"); // Shadow background
            doc.setFillColor(255, 255, 255);
            doc.rect(margin, 7, 30, 30, "F"); // White background
            // Add API logo
            doc.addImage(logoDataUrl, 'PNG', margin + 2, 9, 26, 26);
            console.log("API logo successfully added to PDF");
          } catch (error) {
            console.error('Error adding API logo to PDF:', error.message);
            throw error;
          }
        } catch (error) {
          console.error('Error fetching or processing logo:', error.message);
          // Add debug text to indicate logo failure
          doc.setFont("times", "bold");
          doc.setFontSize(10);
          doc.setTextColor(200, 0, 0);
          doc.text("Logo Failed", margin, 50);
          doc.setTextColor(0, 0, 0);
        }

        // Header
        doc.setFont("times", "bold");
        doc.setFontSize(18);
        doc.setTextColor(0, 51, 102);
        doc.text(schoolName, pageWidth / 2, 14, { align: "center" });
        // Add divider line
        doc.setLineWidth(0.4);
        doc.setDrawColor(0, 51, 102);
        doc.line(margin + 40, 18, pageWidth - margin - 40, 18);
        // Admission Report below divider
        doc.setFontSize(16);
        doc.text("Admission Report", pageWidth / 2, 24, { align: "center" });
        // Add light greyish small line below Admission Report
        doc.setLineWidth(0.2);
        doc.setDrawColor(200, 200, 200);
        doc.line(margin + 85, 27, pageWidth - margin - 85, 27);
        doc.setFontSize(12);
        doc.setFont("times", "normal");
        doc.setTextColor(0, 0, 0);
        doc.text(`Financial Year: ${sessionYear}`, pageWidth / 2, 35, { align: "center" });
        doc.text(`Date Range: ${formattedFromDate} to ${formattedToDate}`, pageWidth / 2, 41, { align: "center" });

        // Filters (with background)
        doc.setFillColor(245, 245, 245);
        doc.rect(margin, 48, maxWidth, 24, "F");
        doc.setFont("times", "bold");
        doc.setFontSize(10);
        doc.setTextColor(0, 51, 102);
        doc.text("Filters Applied:", margin + 5, 54);
        doc.setFont("times", "normal");
        doc.setTextColor(0, 0, 0);
        // Left column
        doc.text(`Class: ${className}`, margin + 5, 59);
        doc.text(`Caste: ${casteName}`, margin + 5, 64);
        doc.text(`Gender: ${genderName}`, margin + 5, 69);
        // Right column
        doc.text(`Stream: ${streamName}`, pageWidth / 2 + 5, 59);
        doc.text(`EWS: ${ewsStatus}`, pageWidth / 2 + 5, 64);

        // Prepare table data with Serial No
        const tableData = data.map((item, index) => [
          (index + 1).toString(),
          item.admissionNo || "-",
          item.studentName || "-",
          item.mobileNo || "-",
          item.rollNo || "-",
          item.className || "-",
          item.section || "-",
          item.fathersName || "-",
          item.mothersName || "-",
         (item.isTransport?.toUpperCase() === "Y" ? "Yes" : "No"),
          summarizeTransportDetails(item.transportDetails),
          item.casteName || "-",
          item.stream || "-",
          item.languageType || "-",
        (item.gender?.toUpperCase() === "MALE" ? "M" :
 item.gender?.toUpperCase() === "FEMALE" ? "F" : "-"),
         (item.ews?.toUpperCase() === "Y" ? "Yes" : "No"),
(item.isTcIssued?.toUpperCase() === "Y" ? "Yes" : "No"),
          item.studentLedgerNo || "-",
        ]);

        // Define column styles with word wrapping
        const totalWidth = 260;
        const columnStyles = {
          0: { cellWidth: 10, overflow: "linebreak" },    // S.No
          1: { cellWidth: 20, overflow: "linebreak" },   // Admission No
          2: { cellWidth: 22, overflow: "linebreak" },   // Student Name
          3: { cellWidth: 20, overflow: "linebreak" },   // Mobile No
          4: { cellWidth: 12, overflow: "linebreak" },   // Roll No
          5: { cellWidth: 13, overflow: "linebreak" },   // Class
          6: { cellWidth: 11, overflow: "linebreak" },   // Section
          7: { cellWidth: 20, overflow: "linebreak" },   // Father Name
          8: { cellWidth: 20, overflow: "linebreak" },   // Mother Name
          9: { cellWidth: 14, overflow: "linebreak" },   // Transport (Yes/No)
          10: { cellWidth: 25, overflow: "linebreak" },  // Transport Details (NEW COLUMN)
          11: { cellWidth: 15, overflow: "linebreak" },  // Caste
          12: { cellWidth: 16, overflow: "linebreak" },  // Stream
          13: { cellWidth: 19, overflow: "linebreak" },  // Language
          14: { cellWidth: 16, overflow: "linebreak" },  // Gender
          15: { cellWidth: 10, overflow: "linebreak" },  // EWS
          16: { cellWidth: 11, overflow: "linebreak" },   // TC Issued
          17: { cellWidth: 17, overflow: "linebreak" },   // Ledger No.
        };
        // Batch processing for large datasets
        const batchSize = 500;
        for (let i = 0; i < tableData.length; i += batchSize) {
          const batch = tableData.slice(i, i + batchSize);
          doc.autoTable({
            head: [
              [
                "S.No",
                "Admission No",
                "Student Name",
                "Mobile No",
                "Roll No",
                "Class",
                "Sec",
                "Father Name",
                "Mother Name",
                "Transport",
                "Transport Details",
                "Caste",
                "Stream",
                "Language",
                "Gender",
                "EWS",
                "TC",
                "Ledger No.",
              ],
            ],
            body: batch,
            startY: i === 0 ? 80 : doc.lastAutoTable.finalY + 8,
            margin: { left: tableLeftMargin, right: margin },
            styles: {
              font: "times",
              fontSize: 8,
              cellPadding: 3,
              overflow: "linebreak",
              minCellHeight: 8,
              textColor: [33, 33, 33],
            },
            headStyles: {
              fillColor: [0, 51, 102],
              textColor: [255, 255, 255],
              fontSize: 9,
              fontStyle: "bold",
            },
            alternateRowStyles: { fillColor: [230, 240, 255] },
            columnStyles: columnStyles,
            tableWidth: totalWidth,
            pageBreak: "auto",
            showHead: i === 0 ? "firstPage" : "never",
            didDrawPage: (data) => {
              // No divider line or page number
            },
          });
        }

        // Save PDF as Blob
        const pdfBlob = doc.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const reportWindow = window.open(pdfUrl, "_blank");
        if (!reportWindow) {
          // alert("Please allow pop-ups to view the report.");
          setSnackbarMessage('Please allow pop-ups to view the report.');
          setOpenSnackbar(true);
          setSnackbarSeverity("warning");
        }
      })();
    },
    []
  );




  const handleSave = async () => {
    setError(null);
    try {
      const formattedFromDate = formatDate(fromDate);
      const formattedToDate = formatDate(toDate);
      const requestBody = {
        classId: selectedClass || 0,
        casteId: selectedCaste || 0,
        gender: selectedGender || "",
        streamId: selectedStream === 0 ? 0 : selectedStream,
        sessionId: selectedSession || 0,
        isEWS: isEWS === "yes" ? 1 : 0,
        fromDate: formattedFromDate,
        toDate: formattedToDate,
        isAll: selectedOption === "all",
      };
      const response = await fetch(`${apiUrl}/FeeReport/ReportAdmission`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(requestBody),
      });
      if (!response.ok) {
        throw new Error("Please provide proper input");
      }

      const data = await response.json();
      if (data.msg === "Record Not Found") {
        setError("No records found");
        return;
      }

      // Sort data by className before generating report
const sortedData = [...data].sort((a, b) => {
  const classA = a.className || "";
  const classB = b.className || "";
  // Extract numeric part for proper numeric sorting (Class 1, 2, 10 instead of 1, 10, 2)
  const numA = parseInt(classA.match(/\d+/)?.[0] || "0", 10);
  const numB = parseInt(classB.match(/\d+/)?.[0] || "0", 10);
  if (numA !== numB) return numA - numB;
  return classA.localeCompare(classB); // fallback for non-numeric class names
});

setReportData(sortedData);

      // Prepare PDF metadata
      const schoolName = schoolData[0]?.schoolName || "School Name";
      const selectedSessionObj = ddlSession.find(
        (session) => session.financialYearID === selectedSession
      );
      const sessionYear = selectedSessionObj?.finanacialYear || "Unknown";
      const className =
        selectedOption === "class" && selectedClass
          ? ddlClass.find((c) => c.classId === selectedClass)?.className ||
          "All"
          : "All";
      const casteName =
        isCasteFilter && selectedCaste
          ? ddlCaste.find((c) => c.casteId === selectedCaste)?.casteName ||
          "None"
          : "None";
      const genderName =
        isGenderFilter && selectedGender ? selectedGender : "None";
      const streamName =
        isStreamFilter && selectedStream
          ? ddlStream.find((s) => s.streamID === selectedStream)?.streamName ||
          "None"
          : "None";
      const ewsStatus = isEWS === "yes" ? "Yes" : "No";

      // Generate PDF
      generatePDF(
        sortedData,
        schoolName,
        sessionYear,
        formattedFromDate,
        formattedToDate,
        className,
        casteName,
        genderName,
        streamName,
        ewsStatus
      );
    } catch (error) {
      setError("Error generating report: " + error.message);
    }
  };

  const generateCSV = () => {
    if (!reportData || !Array.isArray(reportData) || reportData.length === 0) {
      setError("No report data available");
      return;
    }
    let csvContent = "";
    const keys = Object.keys(reportData[0]);
    csvContent += keys.map((key) => key.toUpperCase()).join(",") + "\n";
    reportData.forEach((item) => {
      const values = keys.map((key) => item[key]);
      csvContent += values.join(",") + "\n";
    });
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    saveAs(blob, "report.csv");
  };

  const buttonStyles = {
    generate: {
      background: "linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)",
      borderRadius: "25px",
      padding: "10px 25px",
      width: "150px",
      boxShadow: "0 3px 5px 2px rgba(33, 203, 243, .3)",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)",
        transform: "translateY(-2px)",
        boxShadow: "0 5px 10px rgba(33, 203, 243, .4)",
      },
    },
    csv: {
      background: "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
      borderRadius: "25px",
      padding: "10px 25px",
      width: "150px",
      boxShadow: "0 3px 5px 2px rgba(244, 67, 54, .3)",
      transition: "all 0.3s ease",
      "&:hover": {
        background: "linear-gradient(45deg, #d32f2f 30%, #e53935 90%)",
        transform: "translateY(-2px)",
        boxShadow: "0 5px 10px rgba(244, 67, 54, .4)",
      },
    },
  };

  const selectStyles = {
    width: "250px",
    background: "linear-gradient(45deg, #ffffff 30%, #f5f5f5 90%)",
    borderRadius: "15px",
    boxShadow: "0 3px 6px rgba(0,0,0,0.1)",
    transition: "all 0.3s ease",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#2196F3",
      borderWidth: "2px",
    },
    "&:hover": {
      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
      transform: "translateY(-2px)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1976D2",
      },
    },
    "&.Mui-focused": {
      boxShadow: "0 6px 12px rgba(33, 150, 243, 0.3)",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#21CBF3",
      },
    },
    "& .MuiSelect-select": {
      padding: "12px 16px",
      fontWeight: 500,
      color: "#333",
    },
  };

  return (
    <Grid
      container
      justifyContent="center"
      spacing={2}
      style={{ margin: "0 auto", maxWidth: "600px" }}
    >
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <RadioGroup
            row
            aria-label="student"
            name="student"
            value={selectedOption}
            onChange={handleOptionChange}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="All Students"
            />
            <FormControlLabel
              value="class"
              control={<Radio />}
              label="Class-wise Students"
            />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        {selectedOption === "class" && (
          <FormControl fullWidth>
            <Select
              value={selectedClass}
              onChange={handleClassChange}
              sx={selectStyles}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Class
              </MenuItem>
              {ddlClass.map((classItem) => (
                <MenuItem key={classItem.classId} value={classItem.classId}>
                  {classItem.className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <RadioGroup row value={isEWS} onChange={handleEWSChange}>
            <FormControlLabel
              value="yes"
              control={<Radio />}
              label="EWS: Yes"
            />
            <FormControlLabel value="no" control={<Radio />} label="EWS: No" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <FormControlLabel
            control={
              <Radio
                checked={isCasteFilter}
                onClick={handleCasteFilterToggle}
              />
            }
            label="Filter by Caste"
          />
        </FormControl>
      </Grid>
      {isCasteFilter && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Select
              value={selectedCaste}
              onChange={handleCasteChange}
              sx={selectStyles}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Caste
              </MenuItem>
              {ddlCaste.map((caste) => (
                <MenuItem key={caste.casteId} value={caste.casteId}>
                  {caste.casteName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <FormControlLabel
            control={
              <Radio
                checked={isGenderFilter}
                onClick={handleGenderFilterToggle}
              />
            }
            label="Filter by Gender"
          />
        </FormControl>
      </Grid>
      {isGenderFilter && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Select
              value={selectedGender}
              onChange={handleGenderChange}
              sx={selectStyles}
              displayEmpty
            >
              <MenuItem value="" disabled>
                Select Gender
              </MenuItem>
              {ddlGender.map((gender) => (
                <MenuItem key={gender.genderId} value={gender.gender}>
                  {gender.gender}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <FormControlLabel
            control={
              <Radio
                checked={isStreamFilter}
                onClick={handleStreamFilterToggle}
              />
            }
            label="Filter by Stream"
          />
        </FormControl>
      </Grid>
      {isStreamFilter && (
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <Select
              value={selectedStream}
              onChange={handleStreamChange}
              sx={selectStyles}
              displayEmpty
            >
              <MenuItem value={0} disabled>
                Select Stream
              </MenuItem>
              {ddlStream.map((stream) => (
                <MenuItem key={stream.streamID} value={stream.streamID}>
                  {stream.streamName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <FormControlLabel
            control={
              <Radio
                checked={isSessionFilter}
                onClick={handleSessionFilterToggle}
              />
            }
            label="Filter by Session"
          />
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <Select
            value={selectedSession}
            onChange={handleSessionChange}
            sx={selectStyles}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select Session
            </MenuItem>
            {ddlSession.map((session) => (
              <MenuItem
                key={session.financialYearID}
                value={session.financialYearID}
              >
                {session.finanacialYear}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="From Date"
          type="date"
          inputRef={fromDateRef}
          value={fromDate ? fromDate.split("/").reverse().join("-") : ""}
          onChange={handleFromDateChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={selectStyles}
          inputProps={{
            min:
              selectedSession &&
              getFinancialYearRange().minDate.toISOString().split("T")[0],
            max:
              selectedSession &&
              getFinancialYearRange().maxDate.toISOString().split("T")[0],
          }}
          disabled={isSessionFilter}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="To Date"
          type="date"
          inputRef={toDateRef}
          value={toDate ? toDate.split("/").reverse().join("-") : ""}
          onChange={handleToDateChange}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={selectStyles}
          inputProps={{
            min:
              selectedSession &&
              getFinancialYearRange().minDate.toISOString().split("T")[0],
            max:
              selectedSession &&
              getFinancialYearRange().maxDate.toISOString().split("T")[0],
          }}
          disabled={isSessionFilter}
        />
      </Grid>
      <Grid
        item
        xs={12}
        container
        spacing={1}
        justifyContent="center"
        sx={{ marginBottom: 1, marginTop: 1 }}
      >
        <Grid item xs={6} sm={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            fullWidth
            size="medium"
            sx={{ ...buttonStyles.generate, minWidth: "150px" }}
            disabled={isGenerateDisabled()}
          >
            Generate Report
          </Button>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button
            variant="contained"
            color="error"
            onClick={generateCSV}
            fullWidth
            size="medium"
            sx={{ ...buttonStyles.csv, minWidth: "150px" }}
          >
            Download CSV
          </Button>
        </Grid>
      </Grid>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default AdmReport;
